import { type RouteRecordRaw } from 'vue-router'
import {
    PERMISSION_READ_COMPANIES,
    PERMISSION_READ_COMPANY_DATA,
    PERMISSION_READ_PORTFOLIOS,
    PERMISSION_WRITE_COMPANIES,
    PERMISSION_WRITE_COMPANY_DATA,
} from '@/vue/helpers/permissions'
import { check, checkForCompany } from '@/vue/router/guards/permission'
import type { Empty, WithReportingYear, WithUserId } from './types'

export interface RouteParams {
    // Analytics mode
    'company.overview': WithReportingYear
    'company.assets': WithReportingYear
    'company.analytics': WithReportingYear
    'company.dashboards.list': WithReportingYear
    'company.dashboards.edit': { dashboard_id: string }
    'company.dashboards.manage': Empty

    // Collection mode
    'company.collection.overview': WithReportingYear
    'company.reports': WithReportingYear
    'company.performance': WithReportingYear
    'company.performance.edit.emissions': WithReportingYear
    'company.performance.edit.energy': WithReportingYear
    'company.settings': Empty
    'company.users.create': Empty
    'company.users.bulk-create': Empty
    'company.users.bulk-edit-access': Empty
    'company.users.edit': WithUserId
}

/**
 * Company routes for analytics mode
 */
export const companyAnalyticsRoutes: RouteRecordRaw[] = [
    {
        path: 'company/:reporting_year?',
        component: () => import('@/vue/pages/analytics/company/Layout.vue'),
        beforeEnter: checkForCompany([PERMISSION_READ_COMPANIES, PERMISSION_READ_COMPANY_DATA, PERMISSION_READ_PORTFOLIOS], 'dashboard'),
        children: [
            {
                path: '',
                redirect: { name: 'company.overview' },
            },
            {
                path: 'overview',
                name: 'company.overview',
                component: () => import('@/vue/pages/company/overview/CompanyOverviewPage.vue'),
                meta: {
                    redirectsTo: 'company.collection.overview',
                    title: 'general.company',
                },
            },
            {
                path: 'assets',
                name: 'company.assets',
                component: () => import('@/vue/pages/company/assets/CompanyAssetsPage.vue'),
                meta: {
                    redirectsTo: 'company.collection.overview',
                    title: 'general.assets',
                    fluid: true,
                },
                beforeEnter: check([], 'company.overview', 'company-level-asset-list-page'),
            },
            {
                path: 'analytics',
                name: 'company.analytics',
                component: () => import('@/vue/pages/company/analytics/CompanyAnalyticsPage.vue'),
                meta: {
                    redirectsTo: 'company.collection.overview',
                    title: 'general.sub-navigation.performance',
                },
                beforeEnter: checkForCompany([PERMISSION_WRITE_COMPANIES, PERMISSION_WRITE_COMPANY_DATA], 'dashboard'),
            },

            // Custom Dashboards
            {
                path: 'dashboards',
                beforeEnter: checkForCompany([PERMISSION_READ_COMPANIES, PERMISSION_READ_COMPANY_DATA], 'company.overview'),
                children: [
                    {
                        path: '',
                        name: 'company.dashboards.list',
                        component: () => import('@/vue/pages/custom/dashboard/CompanyDashboardsListPage.vue'),
                        meta: {
                            redirectsTo: 'company.collection.overview',
                            title: 'general.sub-navigation.custom-dashboards',
                            fluid: true,
                        },
                    },
                    {
                        path: 'manage',
                        name: 'company.dashboards.manage',
                        component: () => import('@/vue/pages/custom/dashboard/CompanyDashboardsManagePage.vue'),
                        meta: {
                            redirectsTo: 'company.collection.overview',
                            title: 'general.sub-navigation.custom-dashboards',
                            fluid: true,
                        },
                    },
                    {
                        path: 'edit/:dashboard_id',
                        name: 'company.dashboards.edit',
                        component: () => import('@/vue/pages/custom/dashboard/CompanyDashboardEditPage.vue'),
                        meta: {
                            redirectsTo: 'company.collection.overview',
                            title: 'general.sub-navigation.custom-dashboards',
                            fluid: true,
                        },
                    },
                ],
            },

            {
                path: ':catchAll(.*)',
                redirect: { name: 'company.overview' },
            },
        ],
    },
]

/**
 * Company routes for collection mode
 */
export const companyCollectionRoutes: RouteRecordRaw[] = [
    {
        path: 'company/:reporting_year?',
        component: () => import('@/vue/pages/collection/company/Layout.vue'),
        beforeEnter: checkForCompany([PERMISSION_READ_COMPANIES, PERMISSION_READ_COMPANY_DATA, PERMISSION_READ_PORTFOLIOS], 'company.overview'),
        children: [
            {
                path: '',
                redirect: { name: 'company.collection.overview' },
            },
            {
                path: 'overview',
                name: 'company.collection.overview',
                component: () => import('@/vue/pages/company/portfolios/CompanyPortfoliosPage.vue'),
                meta: {
                    redirectsTo: 'company.overview',
                    title: 'general.overview',
                },
            },
            {
                path: 'performance',
                name: 'company.performance',
                component: () => import('@/vue/pages/company/performance/CompanyPerformancePage.vue'),
                meta: {
                    redirectsTo: 'company.overview',
                    title: 'general.performance',
                },
            },
            {
                path: 'performance/edit/emissions',
                name: 'company.performance.edit.emissions',
                component: () => import('@/vue/pages/company/performance/tabs/emissions/edit/TabEmissionsEdit.vue'),
                meta: {
                    redirectsTo: 'company.overview',
                    parentRoutes: ['company.performance'],
                },
            },
            {
                path: 'performance/edit/energy',
                name: 'company.performance.edit.energy',
                component: () => import('@/vue/pages/company/performance/tabs/energy/edit/TabEnergyEdit.vue'),
                meta: {
                    redirectsTo: 'company.overview',
                    parentRoutes: ['company.performance'],
                },
            },
            {
                path: 'reports',
                name: 'company.reports',
                component: () => import('@/vue/pages/reports/company/reports/CompanyReportsPage.vue'),
                meta: {
                    redirectsTo: 'company.overview',
                    title: 'general.reports',
                },
                beforeEnter: checkForCompany([PERMISSION_WRITE_COMPANIES, PERMISSION_WRITE_COMPANY_DATA], 'company.settings'),
            },
            {
                path: 'settings',
                name: 'company.settings',
                component: () => import('@/vue/pages/company/settings/CompanySettingsPage.vue'),
                meta: {
                    redirectsTo: 'company.overview',
                    title: 'general.settings',
                },
                beforeEnter: checkForCompany([PERMISSION_READ_COMPANIES, PERMISSION_READ_COMPANY_DATA], 'company.collection.overview'),
            },
            {
                path: 'users/create',
                name: 'company.users.create',
                component: () => import('@/vue/pages/company/settings/components/UpdateUserPage.vue'),
                meta: {
                    title: 'company.users.add-user',
                    redirectsTo: 'company.overview',
                    parentRoutes: ['company.settings'],
                },
            },
            {
                path: 'users/bulk-create',
                name: 'company.users.bulk-create',
                component: () => import('@/vue/pages/company/settings/components/BulkUploadUsersPage.vue'),
                meta: {
                    title: 'company.users.bulk-create',
                    redirectsTo: 'company.overview',
                    parentRoutes: ['company.settings'],
                },
            },
            {
                path: 'users/bulk-edit-access',
                name: 'company.users.bulk-edit-access',
                component: () => import('@/vue/pages/company/settings/components/BulkEditAccessPage.vue'),
                meta: {
                    title: 'company.users.bulk-edit.edit-access',
                    redirectsTo: 'company.overview',
                    parentRoutes: ['company.settings'],
                },
            },
            {
                path: 'users/:user_id/edit',
                name: 'company.users.edit',
                component: () => import('@/vue/pages/company/settings/components/UpdateUserPage.vue'),
                meta: {
                    title: 'company.users.edit-user',
                    redirectsTo: 'company.overview',
                    parentRoutes: ['company.settings'],
                },
            },
        ],
    },
]
